import {
    Alert,
    Box,
    Grid2,
    LinearProgress,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { createRentalDay } from '@openapi/timeslots/timeslots';
import { getDateKeyInTimezone, parseDateTimeLocal } from '../../helpers/formatDateTime';
import { useSlotTemplate } from '../../hooks/useSlotTemplate';
import { resolveSlotLabel } from '../../helpers/slotTemplate';

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun display order

// The backend answers duplicate timeslots with 409 Conflict, so detection
// does not depend on translated message text.
function isDuplicateTimeslotError(error) {
    return (error?.response?.status ?? error?.status) === 409;
}

// Every date in [startDate, endDate] whose weekday is selected.
function generateDates(startDate, endDate, days) {
    if (!startDate || !endDate || !days.length) return [];
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    if (endDate < startDate) return [];

    const dates = [];
    const cur = new Date(Date.UTC(sy, sm - 1, sd));
    const endDt = new Date(Date.UTC(ey, em - 1, ed));
    while (cur <= endDt) {
        if (days.includes(cur.getUTCDay())) {
            dates.push(
                [
                    cur.getUTCFullYear(),
                    String(cur.getUTCMonth() + 1).padStart(2, '0'),
                    String(cur.getUTCDate()).padStart(2, '0'),
                ].join('-'),
            );
        }
        cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return dates;
}

function BulkAddRentalDaysPage() {
    const { t } = useTranslation();
    const { showId } = useParams();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const { template } = useSlotTemplate(showId);
    const [progress, setProgress] = useState({ completed: 0, total: 0 });

    const today = getDateKeyInTimezone(new Date());

    const validateSchema = Yup.object().shape({
        days: Yup.array().of(Yup.number()).min(1, t('common.required')),
        slots: Yup.array().of(
            Yup.object().shape({
                startTime: Yup.string().required(t('common.required')),
            }),
        ),
        startDate: Yup.string().required(t('common.required')),
        endDate: Yup.string()
            .required(t('common.required'))
            .test('endAfterStart', t('timeslots.bulkAdd.endAfterStart'), (endDate, context) => {
                const { startDate } = context.parent;
                if (!startDate || !endDate) return true;
                return endDate >= startDate;
            }),
    });

    const initialSlots = template.map((s) => ({
        slotKey: s.key,
        durationMinutes: s.durationMinutes ?? 60,
        startTime: s.timeOfDay || '09:00',
    }));

    // Failures are carried out of the loop in a variable rather than rethrown
    // from the catch, and progress is set after the try rather than in a
    // `finally`. React Compiler can lower neither a finalizer nor a throw
    // inside try/catch, and either one makes it skip memoizing this whole
    // component - which the repo relies on, since manual memoization is banned.
    const onSubmit = async (values) => {
        const dates = generateDates(values.startDate, values.endDate, values.days);
        let created = 0;
        let skipped = 0;
        let failure = null;

        setProgress({ completed: 0, total: dates.length });

        for (let index = 0; index < dates.length; index += 1) {
            const date = dates[index];
            try {
                await createRentalDay(parseInt(showId, 10), {
                    slots: values.slots.map((slot) => ({
                        slotKey: slot.slotKey,
                        startTime: parseDateTimeLocal(`${date}T${slot.startTime}`),
                        durationMinutes: slot.durationMinutes,
                    })),
                });
                created += 1;
            } catch (error) {
                // A day that already exists is a skip, not a failure - bulk add
                // is re-runnable over an overlapping range on purpose.
                if (isDuplicateTimeslotError(error)) {
                    skipped += 1;
                } else {
                    failure = error;
                }
            }
            setProgress({ completed: index + 1, total: dates.length });
            if (failure) break;
        }

        if (failure) {
            notifyError(failure);
            return;
        }

        navigate(`/shows/${showId}/timeslots`);
        notifySuccess(t('timeslots.notifications.bulkRentalDaysAdded', { created, skipped }));
    };

    return (
        <Formik
            enableReinitialize
            onSubmit={onSubmit}
            initialValues={{
                days: [],
                slots: initialSlots,
                startDate: today,
                endDate: '',
            }}
            validationSchema={validateSchema}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => {
                const previewCount = generateDates(
                    values.startDate,
                    values.endDate,
                    values.days,
                ).length;
                const progressPercent = progress.total
                    ? Math.round((progress.completed / progress.total) * 100)
                    : 0;

                return (
                    <Form>
                        <PageWithTitle
                            title={t('timeslots.bulkRentalDaysName')}
                            headline={
                                <Grid2 display="flex" flexDirection="row" gap={2}>
                                    <Grid2>
                                        <CommonButton
                                            fullWidth
                                            onClick={() => navigate(`/shows/${showId}/timeslots`)}
                                            label={t('common.cancel')}
                                            buttonType="secondary"
                                        />
                                    </Grid2>
                                    <Grid2>
                                        <CommonButton
                                            fullWidth
                                            disabled={
                                                isSubmitting ||
                                                previewCount === 0 ||
                                                values.slots.length === 0
                                            }
                                            type="submit"
                                            label={t('common.save')}
                                            buttonType="primary"
                                        />
                                    </Grid2>
                                </Grid2>
                            }
                        >
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <BlockWithTitle title={t('timeslots.bulkRentalDaysName')}>
                                    {values.slots.length === 0 && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <Alert severity="warning">
                                                {t('shows.columns.slotTemplateRentalOffHint')}
                                            </Alert>
                                        </Grid2>
                                    )}
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('timeslots.bulkAdd.daysLabel')}
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <ToggleButtonGroup
                                                value={values.days}
                                                onChange={(_, newDays) =>
                                                    setFieldValue('days', newDays)
                                                }
                                                aria-label={t('timeslots.bulkAdd.daysLabel')}
                                                size="small"
                                                sx={{ flexWrap: 'wrap', gap: 0.5 }}
                                            >
                                                {DAY_ORDER.map((day) => (
                                                    <ToggleButton key={day} value={day}>
                                                        {t(`timeslots.bulkAdd.days.${day}`)}
                                                    </ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        </Box>
                                        {touched.days && errors.days && (
                                            <Typography variant="caption" color="error">
                                                {errors.days}
                                            </Typography>
                                        )}
                                    </Grid2>
                                    {values.slots.map((slot, index) => {
                                        const slotErr = errors.slots?.[index];
                                        const slotTouched = touched.slots?.[index];
                                        return (
                                            <Grid2 size={{ xs: 12 }} key={slot.slotKey}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    {resolveSlotLabel(template, slot)}
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    type="time"
                                                    label={t('timeslots.columns.startTime')}
                                                    value={slot.startTime}
                                                    onChange={(ev) =>
                                                        setFieldValue(
                                                            `slots.${index}.startTime`,
                                                            ev.target.value,
                                                        )
                                                    }
                                                    InputLabelProps={{ shrink: true }}
                                                    error={
                                                        slotTouched?.startTime &&
                                                        Boolean(slotErr?.startTime)
                                                    }
                                                    helperText={
                                                        slotTouched?.startTime
                                                            ? slotErr?.startTime
                                                            : ''
                                                    }
                                                />
                                            </Grid2>
                                        );
                                    })}
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label={t('timeslots.bulkAdd.startDateLabel')}
                                            value={values.startDate}
                                            inputProps={{ min: today }}
                                            onChange={(ev) =>
                                                setFieldValue('startDate', ev.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            error={touched.startDate && Boolean(errors.startDate)}
                                            helperText={touched.startDate ? errors.startDate : ''}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label={t('timeslots.bulkAdd.endDateLabel')}
                                            value={values.endDate}
                                            inputProps={{ min: values.startDate || today }}
                                            onChange={(ev) =>
                                                setFieldValue('endDate', ev.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            error={touched.endDate && Boolean(errors.endDate)}
                                            helperText={touched.endDate ? errors.endDate : ''}
                                        />
                                    </Grid2>
                                    {previewCount > 0 && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('timeslots.bulkRentalDays.preview', {
                                                    count: previewCount,
                                                })}
                                            </Typography>
                                        </Grid2>
                                    )}
                                    {isSubmitting && progress.total > 0 && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progressPercent}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 1 }}
                                                color="text.secondary"
                                            >
                                                {t('timeslots.bulkAdd.progressLabel', {
                                                    completed: progress.completed,
                                                    total: progress.total,
                                                    percent: progressPercent,
                                                })}
                                            </Typography>
                                        </Grid2>
                                    )}
                                </BlockWithTitle>
                            </Grid2>
                        </PageWithTitle>
                    </Form>
                );
            }}
        </Formik>
    );
}

export default BulkAddRentalDaysPage;
